import React, { useState, useCallback, useMemo } from 'react';
import { Plus, Download, Trash2, RefreshCw, AlertTriangle, CheckCircle, Copy } from 'lucide-react';

const FrpcConfigGenerator = () => {
  const [tunnels, setTunnels] = useState([]);
  const [serverInfo] = useState({
    serverAddr: 'frp.foxhank.top',
    serverPort: 7000,
    authToken: 'linuxdo',
    portRange: { min: 10001, max: 60000 }
  });
  
  // 新增：用户凭证状态
  const [userCredentials, setUserCredentials] = useState({
    username: '',
    token: '',
    parseInput: ''
  });
  
  const [checkPortsError, setCheckPortsError] = useState('');
  const [portCheckStatus, setPortCheckStatus] = useState({}); // 存储各个端口的检查状态
  const [portApiUrl] = useState('https://frpcheck.foxhank.cn');

  // 隧道类型配置
  const tunnelTypes = {
    tcp: { name: 'TCP', needsLocalPort: true, needsRemotePort: true },
    udp: { name: 'UDP', needsLocalPort: true, needsRemotePort: true },
    http: { name: 'HTTP', needsLocalPort: true, needsRemotePort: false, needsDomain: true },
    https: { name: 'HTTPS', needsLocalPort: true, needsRemotePort: false, needsDomain: true },
    stcp: { name: 'STCP', needsLocalPort: true, needsRemotePort: false, needsSecretKey: true },
    sudp: { name: 'SUDP', needsLocalPort: true, needsRemotePort: false, needsSecretKey: true }
  };

  // 新增：解析用户凭证
  const parseCredentials = (input) => {
    const regex = /用户名[：:]\s*([^；;]+)；?[；;]?\s*token[：:]\s*([^，,；;]+)/i;
    const match = input.match(regex);
    
    if (match) {
      const username = match[1].trim();
      const token = match[2].trim();
      
      setUserCredentials(prev => ({
        ...prev,
        username,
        token
      }));
    }
  };

  // 新增：处理解析输入变化
  const handleParseInputChange = (value) => {
    setUserCredentials(prev => ({
      ...prev,
      parseInput: value
    }));
    
    // 自动解析
    parseCredentials(value);
  };

  // 检查单个端口是否被占用（用于其他地方调用）
  const checkSinglePort = async (port) => {
    try {
      const response = await fetch(`${portApiUrl}/check/${port}`);
      if (response.ok) {
        const data = await response.json();
        return data.occupied;
      }
      throw new Error(`API请求失败: ${response.status}`);
    } catch (error) {
      console.warn(`检查端口 ${port} 失败:`, error);
      return false; // 如果检查失败，假设端口可用
    }
  };

  // 检查单个端口是否被占用（带防抖）
  const checkSinglePortWithDebounce = useCallback(
    useMemo(() => {
      const debounce = (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout);
            func(...args);
          };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      };
      
      return debounce(async (port, tunnelId) => {
        if (!port) return;
        
        setPortCheckStatus(prev => ({
          ...prev,
          [tunnelId]: { checking: true, occupied: null }
        }));
        
        try {
          const response = await fetch(`${portApiUrl}/check/${port}`);
          if (response.ok) {
            const data = await response.json();
            setPortCheckStatus(prev => ({
              ...prev,
              [tunnelId]: { checking: false, occupied: data.occupied }
            }));
          } else {
            throw new Error(`API请求失败: ${response.status}`);
          }
        } catch (error) {
          console.warn(`检查端口 ${port} 失败:`, error);
          setPortCheckStatus(prev => ({
            ...prev,
            [tunnelId]: { checking: false, occupied: null }
          }));
        }
      }, 800);
    }, [portApiUrl]),
    [portApiUrl]
  );

  // 获取随机可用端口
  const getRandomPort = async () => {
    try {
      const response = await fetch(`${portApiUrl}/random`);
      if (response.ok) {
        const data = await response.json();
        return data.port;
      }
      throw new Error(`API请求失败: ${response.status}`);
    } catch (error) {
      console.warn('获取随机端口失败:', error);
      // 如果API失败，回退到本地生成
      return generateLocalRandomPort();
    }
  };

  // 本地生成随机端口（备用方法）
  const generateLocalRandomPort = () => {
    const { min, max } = serverInfo.portRange;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // 生成随机字符串作为隧道名称
  const generateRandomName = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result;
    let attempts = 0;
    
    do {
      result = '';
      for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      attempts++;
    } while (tunnels.some(t => t.name === result) && attempts < 100);
    
    return result;
  };

  // 生成随机可用端口
  const generateRandomPort = async () => {
    let port;
    let attempts = 0;
    const maxAttempts = 10;
    
    do {
      port = await getRandomPort();
      const isOccupied = await checkSinglePort(port);
      const isUsedInConfig = tunnels.some(t => t.remotePort === port);
      
      if (!isOccupied && !isUsedInConfig) {
        return port;
      }
      
      attempts++;
    } while (attempts < maxAttempts);
    
    // 如果多次尝试后仍无法找到可用端口，返回随机端口
    console.warn('多次尝试后仍无法找到确定可用的端口，返回随机端口');
    return await getRandomPort();
  };

  // 添加新隧道
  const addTunnel = async () => {
    const randomPort = await generateRandomPort();
    
    const newTunnel = {
      id: Date.now(),
      name: generateRandomName(),
      type: 'tcp',
      localIP: '127.0.0.1',
      localPort: 8080,
      remotePort: randomPort,
      customDomain: '',
      secretKey: '',
      comment: ''
    };
    
    setTunnels([...tunnels, newTunnel]);
  };

  // 删除隧道
  const removeTunnel = (id) => {
    setTunnels(tunnels.filter(tunnel => tunnel.id !== id));
    // 清理该隧道的端口检查状态
    setPortCheckStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[id];
      return newStatus;
    });
  };

  // 更新隧道配置
  const updateTunnel = (id, field, value) => {
    setTunnels(tunnels.map(tunnel => 
      tunnel.id === id ? { ...tunnel, [field]: value } : tunnel
    ));
    
    // 如果更新的是远程端口，自动检查端口状态
    if (field === 'remotePort' && value && !isNaN(value)) {
      checkSinglePortWithDebounce(value, id);
    }
  };

  // 为隧道生成新的随机端口
  const handleRegeneratePort = async (tunnelId) => {
    const newPort = await generateRandomPort();
    updateTunnel(tunnelId, 'remotePort', newPort);
  };

  // 检查是否可以生成配置
  const canGenerateConfig = () => {
    return userCredentials.username.trim() && userCredentials.token.trim() && tunnels.length > 0;
  };

  // 生成toml配置文件内容
  const generateConfig = () => {
    if (!canGenerateConfig()) {
      return '# 请先填写用户名和密码，并添加至少一个隧道配置';
    }

    let config = `# FRP客户端配置文件 - 生成时间: ${new Date().toLocaleString()}
# 服务器信息
serverAddr = "${serverInfo.serverAddr}"
serverPort = ${serverInfo.serverPort}
user = "${userCredentials.username}"
metadatas.token = "${userCredentials.token}"

# 日志配置
log.to = "./frpc.log"
log.level = "info"
log.maxDays = 3

# 管理界面(可选)
webServer.addr = "127.0.0.1"
webServer.port = 7400

`;

    tunnels.forEach(tunnel => {
      config += `\n# ${tunnel.comment || tunnel.name}
[[proxies]]
name = "${tunnel.name}"
type = "${tunnel.type}"`;

      if (tunnelTypes[tunnel.type].needsLocalPort) {
        config += `
localIP = "${tunnel.localIP}"
localPort = ${tunnel.localPort}`;
      }

      if (tunnelTypes[tunnel.type].needsRemotePort) {
        config += `
remotePort = ${tunnel.remotePort}`;
      }

      if (tunnelTypes[tunnel.type].needsDomain && tunnel.customDomain) {
        config += `
customDomains = ["${tunnel.customDomain}"]`;
      }

      if (tunnelTypes[tunnel.type].needsSecretKey && tunnel.secretKey) {
        config += `
secretKey = "${tunnel.secretKey}"`;
      }

      config += '\n';
    });

    return config;
  };

  // 下载配置文件
  const downloadConfig = () => {
    if (!canGenerateConfig()) {
      alert('请先填写用户名和密码，并添加至少一个隧道配置');
      return;
    }
    
    const config = generateConfig();
    const blob = new Blob([config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'frpc.toml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 复制配置到剪贴板
  const copyConfig = async () => {
    if (!canGenerateConfig()) {
      alert('请先填写用户名和密码，并添加至少一个隧道配置');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(generateConfig());
      alert('配置已复制到剪贴板！');
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 复制文本到剪贴板
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('已复制到剪贴板！');
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 代码块组件
  const CodeBlock = ({ title, children, copyText }) => (
    <div className="relative">
      {title && <div className="mb-2">{title}</div>}
      <div className="bg-gray-900 text-green-400 rounded p-3 text-sm relative group">
        <button
          onClick={() => copyToClipboard(copyText)}
          className="absolute top-2 right-2 p-1.5 bg-gray-700 text-gray-300 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-600"
          title="复制到剪贴板"
        >
          <Copy className="w-4 h-4" />
        </button>
        {children}
      </div>
    </div>
  );

  // 检查端口冲突
  const isPortConflicted = (port, tunnelId) => {
    // 检查配置中的重复端口
    const duplicateInConfig = tunnels.filter(t => 
      t.remotePort === parseInt(port) && t.id !== tunnelId
    ).length > 0;
    
    // 检查API返回的占用状态
    const portStatus = portCheckStatus[tunnelId];
    const occupiedOnServer = portStatus && portStatus.occupied === true;
    
    return duplicateInConfig || occupiedOnServer;
  };

  // 获取端口状态显示
  const getPortStatus = (tunnelId) => {
    const status = portCheckStatus[tunnelId];
    if (!status) return null;
    
    if (status.checking) {
      return { type: 'checking', message: '检查中...' };
    }
    
    if (status.occupied === true) {
      return { type: 'error', message: '端口已被服务器占用' };
    }
    
    if (status.occupied === false) {
      return { type: 'success', message: '端口可用' };
    }
    
    return null;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">FRP隧道配置生成器</h1>
          <div className="flex gap-2">
            <button
              onClick={addTunnel}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              <Plus className="w-4 h-4" />
              添加隧道
            </button>
          </div>
        </div>

        {/* 服务器信息 */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-800 mb-2">服务器信息</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><strong>服务器:</strong> {serverInfo.serverAddr}</div>
            <div><strong>端口:</strong> {serverInfo.serverPort}</div>
            <div><strong>端口范围:</strong> {serverInfo.portRange.min}-{serverInfo.portRange.max}</div>
          </div>
          {checkPortsError && (
            <div className="flex items-center gap-2 mt-2 text-orange-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{checkPortsError}</span>
            </div>
          )}
        </div>

        {/* 新增：用户凭证配置 */}
        <div className="bg-orange-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-orange-800 mb-3">用户凭证配置 <span className="text-red-500">*必填</span></h3>
          
          {/* 一键解析输入框 */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              一键解析凭证 
              <span className="text-xs text-gray-500 font-normal">（粘贴分发的凭证文本自动解析）</span>
            </label>
            <input
              type="text"
              value={userCredentials.parseInput}
              onChange={(e) => handleParseInputChange(e.target.value)}
              placeholder="用户名：homo；token：114514"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <div className="text-xs text-gray-600 mt-1">
              请前往 <a href="https://cdk.linux.do/receive/8d1a2125-aae3-4c71-8fa0-059b0c6b6288" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://cdk.linux.do/receive/8d1a2125-aae3-4c71-8fa0-059b0c6b6288</a> 分发站点领取token
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                用户名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userCredentials.username}
                onChange={(e) => setUserCredentials(prev => ({ ...prev, username: e.target.value }))}
                placeholder="请输入用户名"
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                  userCredentials.username.trim() ? 'focus:ring-green-500 border-green-300' : 'focus:ring-orange-500'
                }`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Token <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userCredentials.token}
                onChange={(e) => setUserCredentials(prev => ({ ...prev, token: e.target.value }))}
                placeholder="请输入token"
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                  userCredentials.token.trim() ? 'focus:ring-green-500 border-green-300' : 'focus:ring-orange-500'
                }`}
                required
              />
            </div>
          </div>
          
          {(!userCredentials.username.trim() || !userCredentials.token.trim()) && (
            <div className="flex items-center gap-2 mt-2 text-orange-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">请填写完整的用户名和密码才能生成配置文件</span>
            </div>
          )}
        </div>

        {/* 隧道配置列表 */}
        <div className="space-y-4 mb-6">
          {tunnels.map((tunnel) => (
            <div key={tunnel.id} className="border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold">隧道配置 #{tunnel.id}</h4>
                <button
                  onClick={() => removeTunnel(tunnel.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    隧道名称 
                    <span className="text-xs text-gray-500 font-normal">（唯一标识，自动生成）</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tunnel.name}
                      onChange={(e) => updateTunnel(tunnel.id, 'name', e.target.value)}
                      placeholder="8位随机字符串"
                      className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => updateTunnel(tunnel.id, 'name', generateRandomName())}
                      className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200"
                      title="重新生成随机名称"
                    >
                      🔄
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">协议类型</label>
                  <select
                    value={tunnel.type}
                    onChange={(e) => updateTunnel(tunnel.id, 'type', e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(tunnelTypes).map(([key, config]) => (
                      <option key={key} value={key}>{config.name}</option>
                    ))}
                  </select>
                </div>

                {tunnelTypes[tunnel.type].needsLocalPort && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">本地IP</label>
                      <input
                        type="text"
                        value={tunnel.localIP}
                        onChange={(e) => updateTunnel(tunnel.id, 'localIP', e.target.value)}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">本地端口</label>
                      <input
                        type="number"
                        value={tunnel.localPort}
                        onChange={(e) => updateTunnel(tunnel.id, 'localPort', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                {tunnelTypes[tunnel.type].needsRemotePort && (
                  <div>
                    <label className="block text-sm font-medium mb-1">远程端口</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min={serverInfo.portRange.min}
                        max={serverInfo.portRange.max}
                        value={tunnel.remotePort}
                        onChange={(e) => updateTunnel(tunnel.id, 'remotePort', parseInt(e.target.value))}
                        className={`flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                          isPortConflicted(tunnel.remotePort, tunnel.id) 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'focus:ring-blue-500'
                        }`}
                      />
                      <button
                        onClick={() => handleRegeneratePort(tunnel.id)}
                        className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200"
                        title="生成新的随机端口"
                      >
                        🎲
                      </button>
                    </div>
                    {(() => {
                      const portStatus = getPortStatus(tunnel.id);
                      const isDuplicate = tunnels.filter(t => 
                        t.remotePort === tunnel.remotePort && t.id !== tunnel.id
                      ).length > 0;
                      
                      if (isDuplicate) {
                        return <p className="text-red-500 text-xs mt-1">端口在配置中重复</p>;
                      }
                      
                      if (portStatus) {
                        const { type, message } = portStatus;
                        const colorClass = {
                          checking: 'text-blue-500',
                          error: 'text-red-500',
                          success: 'text-green-600'
                        }[type];
                        
                        return (
                          <p className={`${colorClass} text-xs mt-1 flex items-center gap-1`}>
                            {type === 'checking' && <RefreshCw className="w-3 h-3 animate-spin" />}
                            {type === 'error' && <AlertTriangle className="w-3 h-3" />}
                            {type === 'success' && <CheckCircle className="w-3 h-3" />}
                            {message}
                          </p>
                        );
                      }
                      
                      return null;
                    })()}
                  </div>
                )}

                {tunnelTypes[tunnel.type].needsDomain && (
                  <div>
                    <label className="block text-sm font-medium mb-1">自定义域名</label>
                    <input
                      type="text"
                      value={tunnel.customDomain}
                      onChange={(e) => updateTunnel(tunnel.id, 'customDomain', e.target.value)}
                      placeholder="example.com"
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {tunnelTypes[tunnel.type].needsSecretKey && (
                  <div>
                    <label className="block text-sm font-medium mb-1">密钥</label>
                    <input
                      type="text"
                      value={tunnel.secretKey}
                      onChange={(e) => updateTunnel(tunnel.id, 'secretKey', e.target.value)}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium mb-1">备注</label>
                  <input
                    type="text"
                    value={tunnel.comment}
                    onChange={(e) => updateTunnel(tunnel.id, 'comment', e.target.value)}
                    placeholder="隧道用途说明"
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ))}

          {tunnels.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>暂无隧道配置，点击"添加隧道"开始创建</p>
            </div>
          )}
        </div>

        {/* 操作按钮 */}
        {tunnels.length > 0 && (
          <div className="flex gap-4 mb-6">
            <button
              onClick={downloadConfig}
              disabled={!canGenerateConfig()}
              className={`flex items-center gap-2 px-6 py-3 rounded ${
                canGenerateConfig()
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Download className="w-5 h-5" />
              下载配置文件
            </button>
            <button
              onClick={copyConfig}
              disabled={!canGenerateConfig()}
              className={`flex items-center gap-2 px-6 py-3 rounded ${
                canGenerateConfig()
                  ? 'bg-gray-500 text-white hover:bg-gray-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Copy className="w-5 h-5" />
              复制配置
            </button>
          </div>
        )}

        {/* 配置预览 */}
        {tunnels.length > 0 && (
          <div className="bg-gray-900 text-green-400 rounded-lg p-4">
            <h3 className="text-white mb-2 font-semibold">配置预览:</h3>
            <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
              {generateConfig()}
            </pre>
          </div>
        )}

        {/* 下载安装说明 */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-3">FRP客户端下载安装:</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-blue-700 mb-2">GitHub官方下载:</h4>
              <CodeBlock copyText="wget https://github.com/fatedier/frp/releases/download/v0.64.0/frp_0.64.0_linux_amd64.tar.gz
tar -xzf frp_linux_amd64.tar.gz
cd frp_*_linux_amd64">
                <div className="mb-2"># 下载最新版本 (Linux AMD64)</div>
                <div className="mb-1">wget https://github.com/fatedier/frp/releases/download/v0.64.0/frp_0.64.0_linux_amd64.tar.gz</div>
                <div className="mb-1">tar -xzf frp_linux_amd64.tar.gz</div>
                <div className="mb-1">cd frp_*_linux_amd64</div>
                <div># frpc可执行文件现在就在当前目录中</div>
              </CodeBlock>
            </div>

            <div>
              <h4 className="font-medium text-blue-700 mb-2">如果GitHub链接困难，可以使用此文件:</h4>
              <CodeBlock copyText="wget https://dl.foxhank.cn/frp/frpc
chmod +x frpc">
                <div className="mb-2"># 备用下载链接 (Linux AMD64)</div>
                <div className="mb-1">wget https://dl.foxhank.cn/frp/frpc</div>
                <div>chmod +x frpc</div>
              </CodeBlock>
            </div>

            <div>
              <h4 className="font-medium text-blue-700 mb-2">其他系统:</h4>
              <div className="bg-gray-800 text-gray-300 rounded p-3 text-sm">
                <div className="mb-1"># Windows: 下载 frp_windows_amd64.zip</div>
                <div className="mb-1"># macOS: 下载 frp_darwin_amd64.tar.gz</div>
                <div className="mb-1"># ARM64: 替换为对应的 arm64 版本</div>
                <div>GitHub: https://github.com/fatedier/frp/releases</div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-blue-700 mb-2">生成配置文件 (可选 - 如果没有下载配置文件):</h4>
              <CodeBlock copyText={`cat > frpc.toml << 'EOF'
${canGenerateConfig() ? generateConfig() : `# FRP客户端配置文件 - 生成时间: ${new Date().toLocaleString()}
# 服务器信息
serverAddr = "${serverInfo.serverAddr}"
serverPort = ${serverInfo.serverPort}
metadatas.token = "[请填写密码]"

# 日志配置
log.to = "./frpc.log"
log.level = "info"
log.maxDays = 3

# 管理界面(可选)
webServer.addr = "127.0.0.1"
webServer.port = 7400

# 请添加隧道配置...`}
EOF`}>
                <div className="mb-2"># 使用cat命令创建frpc.toml配置文件</div>
                <div className="mb-1">cat &gt; frpc.toml &lt;&lt; 'EOF'</div>
                <pre className="text-yellow-300 text-sm overflow-x-auto whitespace-pre-wrap">
                  {canGenerateConfig() ? generateConfig() : `# FRP客户端配置文件 - 生成时间: ${new Date().toLocaleString()}
# 服务器信息
serverAddr = "${serverInfo.serverAddr}"
serverPort = ${serverInfo.serverPort}
user = "[请填写用户名]"
metadatas.token = "[请填写密码]"

# 日志配置
log.to = "./frpc.log"
log.level = "info"
log.maxDays = 3

# 管理界面(可选)
webServer.addr = "127.0.0.1"
webServer.port = 7400

# 请添加隧道配置...`}
                </pre>
                <div>EOF</div>
              </CodeBlock>
            </div>

            <div>
              <h4 className="font-medium text-blue-700 mb-2">启动客户端:</h4>
              <CodeBlock copyText="./frpc -c frpc.toml
nohup ./frpc -c frpc.toml > frpc.log 2>&1 &">
                <div className="mb-2"># 在当前目录直接运行</div>
                <div className="mb-1">./frpc -c frpc.toml</div>
                <div className="mb-2"># 后台运行</div>
                <div>nohup ./frpc -c frpc.toml &gt; frpc.log 2&gt;&amp;1 &amp;</div>
              </CodeBlock>
            </div>

            <div>
              <h4 className="font-medium text-blue-700 mb-2">配置为系统服务 (可选):</h4>
              <CodeBlock copyText={`sudo nano /etc/systemd/system/frpc.service

# 添加以下内容到服务文件:
[Unit]
Description=FRP Client
After=network.target
[Service]
Type=simple
User=root
Restart=always
ExecStart=/path/to/your/frpc -c /path/to/your/frpc.toml
[Install]
WantedBy=multi-user.target

# 启用并启动服务
sudo systemctl daemon-reload
sudo systemctl enable frpc
sudo systemctl start frpc`}>
                <div className="mb-2"># 创建systemd服务文件</div>
                <div className="mb-1">sudo nano /etc/systemd/system/frpc.service</div>
                <div className="mb-2"># 添加以下内容到服务文件:</div>
                <div className="text-yellow-300 mb-1">[Unit]</div>
                <div className="text-yellow-300 mb-1">Description=FRP Client</div>
                <div className="text-yellow-300 mb-1">After=network.target</div>
                <div className="text-yellow-300 mb-1">[Service]</div>
                <div className="text-yellow-300 mb-1">Type=simple</div>
                <div className="text-yellow-300 mb-1">User=root</div>
                <div className="text-yellow-300 mb-1">Restart=always</div>
                <div className="text-yellow-300 mb-1">ExecStart=/path/to/your/frpc -c /path/to/your/frpc.toml</div>
                <div className="text-yellow-300 mb-1">[Install]</div>
                <div className="text-yellow-300 mb-2">WantedBy=multi-user.target</div>
                <div className="mb-1"># 启用并启动服务</div>
                <div className="mb-1">sudo systemctl daemon-reload</div>
                <div className="mb-1">sudo systemctl enable frpc</div>
                <div>sudo systemctl start frpc</div>
              </CodeBlock>
            </div>
          </div>
        </div>

        {/* 使用说明 */}
        <div className="mt-6 bg-yellow-50 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-800 mb-2">使用说明:</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• <strong>用户凭证</strong>：必须填写用户名和密码才能生成配置，支持一键解析分发的凭证格式</li>
            <li>• <strong>智能端口管理</strong>：输入端口时系统会自动检查端口占用状态，确保端口可用</li>
            <li>• <strong>隧道名称</strong>：每个隧道必须有唯一的名称，系统已自动生成8位随机字符串确保不重复</li>
            <li>• <strong>TCP/UDP</strong>：适用于普通端口转发，需要指定远程端口（范围：10001-60000）</li>
            <li>• <strong>HTTP/HTTPS</strong>：适用于Web服务，通过域名访问，无需指定远程端口</li>
            <li>• <strong>STCP/SUDP</strong>：安全的点对点连接，需要密钥认证</li>
            <li>• 下载配置文件后，将其保存为frpc.toml并使用frpc启动</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FrpcConfigGenerator;